import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type IframePaymentContextData = {
  iframePaymentStatus: GQL.PaymentFrameParsed | null;
  clearIframePaymentStatus: () => void;
};

const IframePaymentContext = createContext<IframePaymentContextData | undefined>(undefined);

const validTypes = new Set(['ok', 'payment-error', 'internal-server-error', 'read-session-failed']);

function parseIframeResponse(data: GQL.IframeResponse): GQL.PaymentFrameParsed {
  let responseCode = '500';
  let status = 'ERROR';
  let details = '';
  let auxiliary = '';

  switch (data.type) {
    case 'ok':
      responseCode = data.status === 'CONFIRMED' ? '200' : '500';
      status = data.status;
      details = 'Pagamento processado';
      auxiliary = data.correlationId ?? '';
      break;

    case 'payment-error':
      responseCode = data.errorCode || data.errorcode || '400';
      status = 'PAYMENT_ERROR';
      details = data.detail ?? '';
      auxiliary = data.standardCode ?? '';
      break;

    case 'internal-server-error':
      responseCode = String(data.status ?? 500);
      status = 'INTERNAL_SERVER_ERROR';
      details = data.detail ?? '';
      auxiliary = data.extra?.postMessage?.type ?? '';
      break;

    case 'read-session-failed':
      responseCode = '500';
      status = 'READ_SESSION_FAILED';
      details = 'Falha ao ler sessão';
      auxiliary = data.type;
      break;

    default:
      break;
  }

  return { responseCode, status, details, auxiliary };
}

const IframePaymentProvider: React.FC = ({ children }) => {
  const [iframePaymentStatus, setIframePaymentStatus] = useState<GQL.PaymentFrameParsed | null>(null);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      try {
        const data = event.data as GQL.IframeResponse;

        if (!data?.type || !validTypes.has(data.type)) return;

        const parsed = parseIframeResponse(data);
        setIframePaymentStatus(parsed);
      } catch (error) {
        console.error('Erro ao processar mensagem do iframe:', error);
      }
    }

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const value = useMemo(
    () => ({
      iframePaymentStatus,
      clearIframePaymentStatus: () => setIframePaymentStatus(null),
    }),
    [iframePaymentStatus]
  );

  return (
    <IframePaymentContext.Provider value={value}>
      {children}
    </IframePaymentContext.Provider>
  );
};

const useIframePayment = () => {
  const context = useContext(IframePaymentContext);

  if (!context) {
    throw new Error('useIframePayment deve ser usado dentro de IframePaymentProvider');
  }

  return context;
};

export { IframePaymentProvider, useIframePayment };
